// Components
import Box from "@mui/material/Box";
import {
  BoldCaption,
  BoldHeadAbove,
  HEAD_CENTRE_X,
  INK,
  WARM,
} from "./boldHead";

// Where the eyes sit on the head seen from above, and where the target hangs.
const EYE_Y = 73;
const EYE_OFFSET = 15;
const FAR_TARGET_Y = 13;
const NEAR_TARGET_Y = 28;

// How far the deviated eye's sight line misses the target by, in canvas units. Linear up to
// 20Δ and compressed above it, so a large angle stays inside the figure without a small one
// being drawn too small to see.
const drawnOffset = (prismDioptres: number): number =>
  prismDioptres <= 20
    ? Math.min(prismDioptres * 1.5, 30)
    : Math.min(30 + (prismDioptres - 20) * 0.75, 42);

// A vertical deviation drawn as a miss above or below the target is a diagram convention: from
// directly above you could not see one at all. It is drawn this way because the alternative is
// a second figure in a second projection for the one axis that needs it.
const isHorizontal = (direction: string): boolean =>
  !/^(right h|left h|hyper|hypo)/i.test(direction);

// Which way the miss goes. Eso and hyper turn one way, exo and hypo the other.
const directionSign = (direction: string): number =>
  /^(eso|right h|hyper)/i.test(direction) ? 1 : -1;

type AlignmentFigureType = {
  prismDioptres: number;
  // "Eso", "Exo", "RightHyper", "LeftHyper" and so on, straight off the payload.
  direction: string;
  caption: string;
  // Which eye held the target. The other is the one drawn missing it.
  fixingEye?: "Left" | "Right";
  near?: boolean;
};

/**
 * Where the eyes point, drawn: the head from above, one sight line on the target and the other
 * missing it by the measurement.
 *
 * Seen from above, so it is NOT mirrored -- the patient's left eye is on the reader's left. See
 * `BoldHeadAbove`.
 */
const AlignmentFigure = ({
  prismDioptres,
  direction,
  caption,
  fixingEye = "Left",
  near = false,
}: AlignmentFigureType) => {
  const offset = drawnOffset(Math.abs(prismDioptres));
  const horizontal = isHorizontal(direction);
  const sign = directionSign(direction);

  const fixingSide = fixingEye === "Left" ? -1 : 1;
  const deviatedSide = -fixingSide;

  const targetY = near ? NEAR_TARGET_Y : FAR_TARGET_Y;
  const fixingX = HEAD_CENTRE_X + fixingSide * EYE_OFFSET;
  const deviatedX = HEAD_CENTRE_X + deviatedSide * EYE_OFFSET;

  const missX = horizontal
    ? HEAD_CENTRE_X - sign * deviatedSide * offset
    : HEAD_CENTRE_X;
  const missY = horizontal ? targetY : targetY + sign * offset * 0.72;

  // The pupil shifts a little inside its own eye, so the eye reads as turned rather than as
  // merely having a line drawn out of it at an angle.
  const pupilShift = Math.min(offset * 0.1, 2.6);
  const pupilDx = horizontal ? -sign * deviatedSide * pupilShift : 0;
  const pupilDy = horizontal ? 0 : -sign * pupilShift;

  return (
    <Box
      component="svg"
      role="img"
      data-testid="synoptophore-alignment-figure"
      aria-label={caption}
      viewBox="0 4 120 126"
      sx={{ display: "block", width: "100%", mx: "auto" }}
    >
      <BoldHeadAbove />

      <circle cx={HEAD_CENTRE_X} cy={targetY} r={5} fill={INK} />

      {/* The fixing eye's sight line, on the target. */}
      <line
        x1={fixingX}
        y1={EYE_Y}
        x2={HEAD_CENTRE_X}
        y2={targetY}
        stroke={INK}
        strokeWidth={2}
        strokeLinecap="round"
        opacity={0.45}
      />

      {/* The deviated eye's, missing it by the measurement. */}
      <line
        x1={deviatedX}
        y1={EYE_Y}
        x2={missX}
        y2={missY}
        stroke={WARM}
        strokeWidth={3.4}
        strokeLinecap="round"
      />

      {/* The ring is filled white so the thick line stops at its centre rather than running
          past it, which read as a "q" or a "p". */}
      {prismDioptres > 0 && (
        <circle
          cx={missX}
          cy={missY}
          r={4.6}
          fill="#FFFFFF"
          stroke={WARM}
          strokeWidth={2.6}
        />
      )}

      {[
        { x: fixingX, dx: 0, dy: 0, deviated: false },
        { x: deviatedX, dx: pupilDx, dy: pupilDy, deviated: true },
      ].map((eye) => (
        <g key={eye.deviated ? "deviated" : "fixing"}>
          <circle
            cx={eye.x}
            cy={EYE_Y}
            r={8}
            fill="#FFFFFF"
            stroke={eye.deviated ? WARM : INK}
            strokeWidth={2.6}
          />
          <circle
            cx={eye.x + eye.dx}
            cy={EYE_Y - 2 + eye.dy}
            r={3.4}
            fill={INK}
          />
        </g>
      ))}

      <BoldCaption text={caption} />
    </Box>
  );
};

export default AlignmentFigure;
